import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Check } from 'lucide-react';

import { useWizardState } from './useWizardState';
import { WizardShell } from './WizardShell';
import { FunctionsHttpError } from '@supabase/supabase-js';

import { supabase } from '../../lib/supabase';
import { teamMemberQueryKey } from '../../app/useTeamMember';
import styles from './wizard.module.css';

const STEPS = ['ברוך הבא', 'פרטי העסק', 'לוקליזציה', 'סיכום'];

interface BootstrapResponse {
  success?: boolean;
  organization_id?: string;
  error?: string;
  field?: string;
  message?: string;
  // Present on 409 already_bootstrapped. 'invited' / 'suspended' means the
  // member row exists but RLS hides it from its own owner, so navigating to "/"
  // would bounce straight back to this wizard.
  status?: string;
}

export function Step4Confirm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const data = useWizardState((s) => s.data);
  const reset = useWizardState((s) => s.reset);
  const prev = useWizardState((s) => s.prev);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const onSubmit = async () => {
    setFormError(null);
    setSubmitting(true);
    const result = await supabase.functions.invoke<BootstrapResponse>('bootstrap_org', {
      body: {
        organization: {
          name: data.org_name,
          business_id: data.business_id || undefined,
          vat_id: data.vat_id || undefined,
        },
        profile: { full_name: data.full_name },
      },
    });
    // Any non-2xx reply — including the 409 already_bootstrapped one this
    // wizard depends on — arrives as a FunctionsHttpError with data === null,
    // so the body has to be read off the error's Response. Without this the
    // already_bootstrapped branch below was unreachable and a returning user
    // only ever saw a generic "try again", with no way forward.
    let response: BootstrapResponse | null = result.data ?? null;
    if (!response && result.error instanceof FunctionsHttpError) {
      // `context` is typed `any` by functions-js; it is the fetch Response.
      const failed = result.error.context as Response;
      response = (await failed.json().catch(() => null)) as BootstrapResponse | null;
    }

    if (!response) {
      setSubmitting(false);
      setFormError('אירעה שגיאה. נסה שוב.');
      return;
    }

    if (response.error === 'already_bootstrapped') {
      // Only an ACTIVE membership is safe to treat as success. Any other status
      // is invisible to RLS (current_organization_id() filters status='active'),
      // so "/" would send the user back here and the wizard would loop forever
      // with no explanation. Say what is wrong and stop.
      if (response.status && response.status !== 'active') {
        setSubmitting(false);
        setFormError(
          response.status === 'invited'
            ? 'ההזמנה שלך לארגון עוד לא אושרה. פנה למנהל הארגון כדי להפעיל את החשבון.'
            : 'החשבון שלך מושהה. פנה למנהל הארגון.',
        );
        return;
      }
      await queryClient.invalidateQueries({ queryKey: teamMemberQueryKey });
      reset();
      navigate('/', { replace: true });
      return;
    }

    if (!response.success) {
      setSubmitting(false);
      setFormError('אירעה שגיאה. נסה שוב.');
      return;
    }

    await queryClient.invalidateQueries({ queryKey: teamMemberQueryKey });
    reset();
    navigate('/', { replace: true });
  };

  return (
    <WizardShell
      step={3}
      steps={STEPS}
      foot={
        <>
          <button type="button" className={styles.btnSecondary} onClick={prev} disabled={submitting}>
            <ArrowRight size={14} className="flip-rtl" />
            חזור
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => { void onSubmit(); }}
            disabled={submitting}
          >
            <Check size={14} />
            צור את העסק שלי
          </button>
        </>
      }
    >
      <h3>סיכום</h3>
      <p className={styles.help}>בדוק שהפרטים נכונים. אפשר לחזור ולתקן בכל שלב.</p>

      <div className={styles.summary}>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>השם המלא</span>
          <span className={styles.summaryValue}>{data.full_name}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>שם העסק</span>
          <span className={styles.summaryValue}>{data.org_name}</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>ע.מ. / ח.פ.</span>
          <span className={styles.summaryValue}>
            {data.business_id ? (
              <span className="num">{data.business_id}</span>
            ) : (
              <span className={styles.summaryEmpty}>לא הוגדר</span>
            )}
          </span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>עוסק מורשה</span>
          <span className={styles.summaryValue}>
            {data.vat_id ? (
              <span className="num">{data.vat_id}</span>
            ) : (
              <span className={styles.summaryEmpty}>לא הוגדר</span>
            )}
          </span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>אזור זמן</span>
          <span className={styles.summaryValue}>Asia/Jerusalem</span>
        </div>
        <div className={styles.summaryRow}>
          <span className={styles.summaryLabel}>שפה · מטבע</span>
          <span className={styles.summaryValue}>עברית · ILS</span>
        </div>
      </div>

      {formError ? <p className={styles.formError}>{formError}</p> : null}
    </WizardShell>
  );
}
